import { useCallback, useEffect, useRef, useState } from "react";

// navigator.wakeLock.request('screen') для long-running операций.
// Auto-release при page hidden + re-acquire при visible.

type WakeLockType = "screen";

interface WakeLockSentinelLike {
	released: boolean;
	release: () => Promise<void>;
	addEventListener: (type: "release", listener: () => void) => void;
}

interface WakeLockApi {
	request: (type: WakeLockType) => Promise<WakeLockSentinelLike>;
}

function getWakeLock(): WakeLockApi | null {
	if (typeof navigator === "undefined") return null;
	const wl = (navigator as Navigator & { wakeLock?: WakeLockApi }).wakeLock;
	return wl ?? null;
}

export interface UseWakeLockResult {
	isActive: boolean;
	isSupported: boolean;
	acquire: () => Promise<void>;
	release: () => Promise<void>;
}

export function useWakeLock(autoAcquire = false): UseWakeLockResult {
	const sentinel = useRef<WakeLockSentinelLike | null>(null);
	const [isActive, setIsActive] = useState(false);
	const isSupported = getWakeLock() !== null;

	const acquire = useCallback(async () => {
		const api = getWakeLock();
		if (!api) return;
		if (sentinel.current && !sentinel.current.released) return;
		try {
			const s = await api.request("screen");
			sentinel.current = s;
			setIsActive(true);
			s.addEventListener("release", () => {
				setIsActive(false);
			});
		} catch {
			setIsActive(false);
		}
	}, []);

	const release = useCallback(async () => {
		const s = sentinel.current;
		if (!s || s.released) {
			setIsActive(false);
			return;
		}
		await s.release();
		sentinel.current = null;
		setIsActive(false);
	}, []);

	// Re-acquire при возврате на foreground (browser теряет wake lock при hide).
	useEffect(() => {
		if (typeof document === "undefined") return;
		const onVisibility = () => {
			if (document.visibilityState === "visible" && autoAcquire) {
				void acquire();
			}
		};
		document.addEventListener("visibilitychange", onVisibility);
		return () => document.removeEventListener("visibilitychange", onVisibility);
	}, [autoAcquire, acquire]);

	useEffect(() => {
		if (autoAcquire) void acquire();
		return () => {
			void release();
		};
	}, [autoAcquire, acquire, release]);

	return { isActive, isSupported, acquire, release };
}
